<template>
  <action-dispatcher
    action="lessons/getAll"
    :parameters="$route.params.id"
    class="view-lesson"
  >
    <div v-if="lesson" class="container-fluid pr-lg-5 py-3 order-lg-1">
      <div class="animate first-section">
        <header v-if="lesson.module && lesson.module.name" class="d-flex mb-3">
          <button
            class="btn btn-back text-decoration-none p-0 m-0"
            @click="goBack"
          >
            <i class="icon-chevron-left mr-2"></i> {{ lesson.module.name }}
          </button>
        </header>

        <div class="row" :class="{ 'justify-content-center': isZoomLink }">
          <div v-if="isZoomLink" class="col-lg-12">
            <meeting :lesson="lesson" />
          </div>
          <div v-else class="col-lg-8 order-0">
            <empty-content v-if="!lesson.link_media && !lesson.description" />
            <video-player
              v-else-if="lesson.link_media"
              ref="video-player"
              :lesson="lesson"
              @save-progress="saveProgress"
              @ended="goToNextLesson"
            />
          </div>
          <div
            v-if="!isZoomLink"
            class="col-lg-4 d-none d-lg-block"
            :class="{ 'order-lg-2': !lesson.link_media && lesson.description }"
          >
            <b-tabs justified class="lesson-tabs">
              <b-tab active v-if="!isZoomLink">
                <template #title>
                  <i class="fa-regular fa-note-sticky mr-2"></i>
                  Anotações
                </template>
                <tab-notations
                  :lesson="lesson"
                  class=""
                  @play="play()"
                  @pause="pause()"
                />
              </b-tab>
              <b-tab class="lesson-list-title">
                <template #title>
                  <i class="fa-solid fa-list-ul mr-2"></i>
                  Conteúdo
                </template>
                <tab-lesson-line-list :items="items" :current="lesson" />
              </b-tab>
            </b-tabs>
          </div>
          <div
            class="col-lg-8 animate second-section"
            :class="{ 'order-lg-1': !lesson.link_media && lesson.description }"
          >
            <content-description
              :content="lesson"
              :finishedLesson="finishedLesson"
              :loadingFinish="loadingFinish"
              :nextContent="nextLesson"
              :previousContent="previousLesson"
              @toggleFinished="toggleFinished"
            />
            <div class="d-lg-none">
              <b-tabs justified class="lesson-tabs">
                <b-tab active v-if="!isZoomLink">
                  <template #title>
                    <i class="fa-regular fa-note-sticky mr-2"></i>
                    Anotações
                  </template>
                  <tab-notations :lesson="lesson" class="" />
                </b-tab>
                <b-tab class="lesson-list-title">
                  <template #title>
                    <i class="fa-solid fa-list-ul mr-2"></i>
                    Conteúdo
                  </template>
                  <tab-lesson-line-list :items="items" :current="lesson" />
                </b-tab>
              </b-tabs>
            </div>
            <lesson-material :lesson="lesson" />

            <div v-if="lesson.comments_enabled">
              <send-comment :lesson="$route.params.lesson" @success="reload" />

              <div class="d-flex align-items-center">
                <div class="flex-fill mt-5 mb-3">
                  Comentários ({{ commentsList.length }})
                </div>
                <b-spinner v-if="silentLoading" variant="primary" small />
              </div>
              <action-dispatcher
                action="forum/getAll"
                :parameters="{ params }"
                ref="forumDispatcher"
                @success="forumSuccess"
              >
                <div
                  v-for="comment in commentsList"
                  :key="'c' + comment.id"
                  class="mb-5"
                  :class="{ 'divider-border-bottom': commentsList.length > 1 }"
                >
                  <comment
                    class="my-3"
                    :comment="comment"
                    @success="reload"
                    @success-noreload="silentReload"
                  />
                </div>
              </action-dispatcher>

              <div class="text-center py-4">
                <button
                  v-if="commentsList.length > 0 && showLoadMore"
                  class="btn btn-next px-5"
                  @click="loadMore"
                >
                  <loading :show="loadingMore">Carregar mais</loading>
                </button>
              </div>
            </div>
          </div>
          <div class="col-lg-4 d-none d-lg-block" v-if="isZoomLink">
            <b-tabs class="p-4">
              <b-tab
                title="Conteúdo do módulo"
                class="lesson-list-title"
                active
              >
                <tab-lesson-line-list :items="items" :current="lesson" />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
  </action-dispatcher>
</template>

<script>
import Comment from "@/components/lesson/Comment";
import ContentDescription from "@/components/lesson/ContentDescription";
import EmptyContent from "@/components/lesson/EmptyContent";
import LessonMaterial from "@/components/lesson/LessonMaterial";
import SendComment from "@/components/lesson/SendComment";
import TabLessonLineList from "@/components/lesson/TabLessonLineList";
import TabNotations from "@/components/lesson/TabNotations";
import VideoPlayer from "@/components/lesson/VideoPlayer";
import get from "lodash/get";
import { mapState } from "vuex";
const Meeting = () => import("@/components/lesson/Meeting.vue");

export default {
  components: {
    ContentDescription,
    TabLessonLineList,
    LessonMaterial,
    TabNotations,
    VideoPlayer,
    EmptyContent,
    SendComment,
    Meeting,
    Comment
  },
  data() {
    return {
      loadingFinish: false,
      silentLoading: false,
      tab: "chapters",
      params: {
        id: this.$route.params.lesson,
        limit: 10,
        offset: 0
      },
      loadingMore: false,
      showLoadMore: false,
      typing: false
    };
  },
  computed: {
    ...mapState("forum", ["comments", "count"]),
    ...mapState("modules", ["modules"]),
    ...mapState("lessons", ["lessons"]),
    ...mapState("quiz", ["quizes"]),
    ...mapState(["setup"]),
    items() {
      return [...this.lessons, ...this.quizes].sort((a, b) => a.sort - b.sort);
    },
    commentsList() {
      return this.comments.filter(
        c => !(c.status == "review" && c.user_id !== this.$user.user.id)
      );
    },
    isZoomLink() {
      if (!this.lesson || !this.lesson.link_media) {
        return null;
      }

      const zoomUrlRegex = /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/g;
      return this.lesson.link_media.match(zoomUrlRegex);
    },
    lesson() {
      if (!this.lessons) {
        return null;
      }

      const data = this.lessons.find(
        lesson => lesson.id === this.$route.params.lesson
      );

      return data;
    },
    lessonIndex() {
      return this.items.findIndex(i => i.id === this.$route.params.lesson);
    },
    nextLesson() {
      //if there's a next content in the current module/submodule
      if (this.items[this.lessonIndex + 1]) {
        return {
          ...this.items[this.lessonIndex + 1],
          module: {
            id: this.$route.params.id
          }
        };
      }

      const inSubmodule =
        this.modules.findIndex(
          m => m.id == this.items[this.lessonIndex].module.id
        ) == -1;

      // if the current lesson is inside a submodule
      if (inSubmodule) {
        const parent_module = this.modules.find(module =>
          module.sub_modules.find(
            sub_module => sub_module.id === this.lesson.module.id
          )
        );

        const sub_module_index = parent_module.sub_modules.findIndex(
          submodule => submodule.id == this.lesson.module.id
        );

        //if there's a next submodule with content
        if (
          parent_module.sub_modules[sub_module_index + 1] &&
          parent_module.sub_modules[sub_module_index + 1].items.length
        ) {
          return {
            ...parent_module.sub_modules[sub_module_index + 1].items[0],
            module: {
              id: parent_module.sub_modules[sub_module_index + 1].id
            }
          };
        }
      }

      const module_index = inSubmodule
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.lesson.module.id
            )
          )
        : this.modules.findIndex(module => module.id == this.lesson.module.id);

      //if there's a submodule with content in the current module
      if (
        !inSubmodule &&
        this.modules[module_index].sub_modules.length &&
        this.modules[module_index].sub_modules[0].items.length
      ) {
        return {
          ...this.modules[module_index].sub_modules[0].items[0],
          module: {
            id: this.modules[module_index].sub_modules[0].id
          }
        };
      }

      const nextModule = this.modules[module_index + 1];

      //if there's a next module with content
      if (nextModule && nextModule.items.length) {
        const moduleFirstLesson = nextModule.lessons[0];
        const submoduleFirstLesson = nextModule.sub_modules[0]?.lessons[0];
        //if there's a lesson in the next module (outside a submodule)
        if (moduleFirstLesson) {
          return {
            ...moduleFirstLesson,
            module: {
              id: nextModule.id
            }
          };
        }
        //if there's a submodule with lesson in the next module
        if (submoduleFirstLesson) {
          return {
            ...submoduleFirstLesson,
            module: {
              id: nextModule.sub_modules[0]?.id
            }
          };
        }
      }

      return null;
    },
    previousLesson() {
      if (this.items[this.lessonIndex - 1])
        return {
          ...this.items[this.lessonIndex - 1],
          module: {
            id: this.$route.params.id
          }
        };

      const module_index = this.modules.findIndex(
        m => m.id == this.items[this.lessonIndex].module.id
      );

      if (
        this.modules[module_index - 1] &&
        this.modules[module_index - 1].items.length
      ) {
        return {
          ...this.modules[module_index - 1].items[
            this.modules[module_index - 1].items.length - 1
          ],
          module: this.modules[module_index - 1]
        };
      }

      return null;
    },
    finishedLesson() {
      return !!(
        this.lesson &&
        this.lesson.progress &&
        this.lesson.progress.finished_at
      );
    },
    chapters() {
      return get(this.lesson, "chapters", []).sort((a, b) => {
        if (a.time > b.time) {
          return 1;
        }
        if (b.time > a.time) {
          return -1;
        }
        return 0;
      });
    }
  },
  mounted() {
    this.$store.dispatch("modules/getAll").then();
    this.resetComments();
    this.dispatchQuizzes();
  },
  beforeRouteLeave(to, from, next) {
    // Force a final progress save before the route unmounts so the lesson
    // shows up in "Continuar assistindo" even if the student leaves between
    // heartbeat ticks. Fire-and-forget — never block navigation.
    try {
      const player = this.$refs["video-player"];
      if (player && typeof player.flushProgress === "function") {
        player.flushProgress();
      }
    } finally {
      next();
    }
  },
  methods: {
    play() {
      if (this.typing) {
        setTimeout(() => {
          this.$refs["video-player"].player.play();
        }, 200);
        this.typing = false;
      }
    },
    pause() {
      if (!this.typing) this.$refs["video-player"].player.pause();
      this.typing = true;
    },
    goBack() {
      if (window.clientZoom) {
        window.clientZoom.leaveMeeting();
      }

      this.$router.push({
        name: "module",
        params: { id: this.$route.params.id }
      });
    },
    goToNextLesson() {
      if (this.nextLesson) {
        this.$router.push({
          params: { lesson: this.nextLesson.id }
        });
      }
    },
    resetComments() {
      this.showLoadMore = true;
      this.params.offset = 0;
      this.$store.commit("forum/setComments", []);
    },
    reload() {
      this.resetComments();
      this.$refs.forumDispatcher.dispatch();
    },
    silentReload() {
      this.silentLoading = true;
      this.params.offset = 0;
      this.$store
        .dispatch("forum/getAll", {
          params: this.params,
          replaceComments: true
        })
        .then()
        .finally(() => {
          this.silentLoading = false;
        });
    },
    forumSuccess(data) {
      this.showLoadMore = !!data?.next;
    },
    dispatchQuizzes() {
      this.$store
        .dispatch("quiz/getAll", {
          module_id: this.$route.params.id,
          params: { limit: 1000, status: "published" }
        })
        .then();
    },
    loadMore() {
      this.loadingMore = true;
      this.params.offset += this.params.limit;
      this.$store
        .dispatch("forum/getAll", { params: this.params })
        .then(data => {
          this.showLoadMore = !!data?.next;
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    clickChapter(chapter) {
      const timeArr = chapter.time.split(":").map(str => parseInt(str));
      this.$refs["video-player"].player.currentTime =
        timeArr[0] * 60 * 60 + timeArr[1] * 60 + timeArr[2];
    },
    toggleFinished() {
      this.saveProgress({ finished: !this.finishedLesson, duration: 0 }, true);
    },
    saveProgress({ finished, duration }, refresh = false) {
      if (refresh) {
        this.loadingFinish = true;
      }
      this.$store
        .dispatch("modules/setProgress", {
          lesson_id: this.lesson.id,
          stopped_at: parseInt(duration),
          finished_at: finished
        })
        .then(() => {
          if (refresh) {
            setTimeout(() => {
              this.$store
                .dispatch("lessons/getAll", this.$route.params.id)
                .finally(() => {
                  this.loadingFinish = false;
                });
            }, 1000);
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.view-lesson {
  padding-top: 72px;
  min-height: calc(100vh - 56px);

  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }

  iframe {
    height: 500px;
  }
  .tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  ::v-deep .tab-content,
  .tab-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  // Estilos melhorados para as abas
  ::v-deep .lesson-tabs {
    .nav-tabs {
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 0;

      .nav-link {
        border: none;
        border-radius: 0;
        padding: 12px 16px;
        font-size: 13px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.5);
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 14px;
          transition: transform 0.3s ease;
        }

        &:hover {
          color: rgba(255, 255, 255, 0.8);
          background: rgba(62, 86, 172, 0.08);

          i {
            transform: scale(1.1);
          }
        }

        &.active {
          color: #ab85e5;
          background: transparent;
          border-bottom: 2px solid #ab85e5;

          i {
            color: #ab85e5;
          }
        }
      }
    }

    .tab-content {
      padding-top: 8px;
    }
  }

  .animate {
    animation: fadeInAnimation ease 1s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
    &.first-section {
      animation-delay: 0s;
      opacity: 0;
    }
    &.second-section {
      animation-delay: 1s;
      opacity: 0;
    }
  }

  @keyframes fadeInAnimation {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>
